import { useEffect } from "react";
import { useParams } from 'react-router-dom';
import { useSpinnerContext, useSessionContext } from '@contexts';
import { sunboundPayProxy } from '@proxies';
import SetupLogin from "./setup-login";
import SetupCredentials from "./setup-credentials";
import { useLocation, useNavigate } from 'react-router-dom';
import { helpers, Constants } from "@shared";
import { useAllowedPaymentTypes } from "../hooks/useAllowedPaymentTypes/useAllowedPaymentTypes";
import type { Account, RawSession } from '@app-types';

type AccountWithSession = Account & Omit<RawSession, `accounts`>;

const Signup = () => {

    const location = useLocation(), navigate = useNavigate();
    const { setSpinnerShow } = useSpinnerContext();
    const params = useParams<{ sessionId: string }>();
    const { sessionId } = params;
    const {
        session,
        setSession,
        accounts,
        rawSession
    } = useSessionContext();
    const { allowedPaymentTypes, isError: allowedPaymentTypesIsError } = useAllowedPaymentTypes();

    useEffect(() => {
        (async () => {
            if (!sessionId) return;

            let hasError = false;
            setSpinnerShow(true);
            try {
                const response = await sunboundPayProxy.getSession(sessionId);
                setSession(response);
            } catch (error) {
                console.error(error);
                hasError = true;
            }
            setSpinnerShow(false);
            if (hasError)
                helpers.navigateStart(location, navigate, Constants.Flows.resendWelcome.id, undefined, undefined, params);
        })();
    }, []);

    const next = () => {

        if (accounts.length > 1) {

            if (!rawSession) return;

            let sessionAccounts: AccountWithSession[];
            {
                const { accounts, ...rest } = rawSession;
                sessionAccounts = accounts.map(account => ({ ...account, ...rest }));
            }

            const [account, ...rest] = sessionAccounts;
            const { hasPaymentMethods } = account || {};

            helpers.navigateStart(location, navigate, Constants.Flows.setupMultiple.id, {
                missingAccounts: rest.length !== 0,
                hasPaymentMethods
            }, {
                account,
                accounts,
                setupAccounts: [...rest],
                hasMultipleAccounts: true
            }, params);
        } else {
            const {
                paymentPreferences: { hasDefaultPaymentMethod = false, defaultPaymentMethod = null } = {},
                accountPreferences: { isAutoPay = false } = {},
                hasPaymentMethods = false
            } = session || {};

            const defaultPaymentValid = (defaultPaymentMethod?.idPaymentType !== undefined && allowedPaymentTypes.includes(defaultPaymentMethod.idPaymentType)) || allowedPaymentTypesIsError;

            helpers.navigateStart(location, navigate, Constants.Flows.setupSingle.id, {
                hasDefaultPaymentMethod: hasDefaultPaymentMethod && defaultPaymentValid,
                hasPaymentMethods,
                isAutoPay
            }, {
                defaultPaymentMethod,
                account: { ...session },
                hasMultipleAccounts: false
            }, params);
        }
    };

    let renderComponent = null;
    if (session) {
        const { email = `` } = session;
        renderComponent = session.hasPassword ?
            <SetupLogin initial={{ email }} success={next} /> : <SetupCredentials initial={{ email }} success={next} />;
    }

    return renderComponent;

};

export default Signup;
